import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ScoreStatus, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineXS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { NewsTitleContent } from '@sorare/core/src/components/player/News/NewsTitleContent';
import { NewsUpdates } from '@sorare/core/src/components/player/News/NewsUpdates';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { useAnySportEvents } from '@sorare/marketplace/src/shared/events/lib';
import { isGameStarted } from '@sorare/marketplace/src/shared/game/lib';
import { PrimaryBuyForAPlayer } from '@sorare/marketplace/src/shared/player/PrimaryBuyForAPlayer';
import { BriefGame } from '@sorare/marketplace/src/shared/stats/BriefGame';
import { PlayerGameScoreStats } from '@sorare/marketplace/src/shared/stats/PlayerGameScoreStats';

import { BriefSo5AppearanceScore } from '#src/components/stats/BriefSo5AppearanceScore/index.tsx';

import type {
  AppearanceDialogQuery,
  AppearanceDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const SO5_APPEARANCE_DIALOG_QUERY = gql`
  query AppearanceDialogQuery($id: String!) {
    so5 {
      composeTeamBenchObject(id: $id) {
        id
        ... on So5Appearance {
          id
          so5Fixture {
            slug
            startDate
            endDate
          }
        }
        ... on AppearanceInterface {
          id
          scoreStatus
        }
        anyPlayer {
          slug
          displayName
          news(last: 1) {
            ...NewsUpdates_PlayerNewsUpdate
          }
        }
        eligiblePlayerGameScores {
          id
          anyGame {
            id
            statusTyped
            ...BriefGame_anyGameInterface
          }
          anyPlayerGameStats {
            id
            anyTeam {
              slug
              ...BriefGame_teamInterface
            }
          }
          ...PlayerGameScoreStats_playerGameScoreInterface
        }
        pictureUrl
        bonus
        ...BriefSo5AppearanceScore_so5Appearance
      }
    }
  }
  ${PlayerGameScoreStats.fragments.playerGameScoreInterface}
  ${BriefGame.fragments.anyGameInterface}
  ${BriefGame.fragments.teamInterface}
  ${NewsUpdates.fragments.PlayerNewsUpdate}
  ${BriefSo5AppearanceScore.fragments.so5Appearance}
` as TypedDocumentNode<AppearanceDialogQuery, AppearanceDialogQueryVariables>;

const Root = styled(Vertical)`
  --container-padding: var(--double-unit);
  padding: var(--container-padding);
  @media ${laptopAndAbove} {
    min-width: 600px;
  }
  &.loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 675px;
  }
`;

const NewsUpdatesWrapper = styled(Vertical)`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-100-opaque);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  so5AppearanceId: string;
  showUpsell?: boolean;
  onClickGame?: (gameId: string) => void;
};

export const So5AppearanceDialog = ({
  open,
  onClose,
  showUpsell,
  so5AppearanceId,
  onClickGame,
}: Props) => {
  const { sport } = useSportContext();
  const { formatDate } = useIntlContext();
  const track = useAnySportEvents();
  const isLaptopAndAbove = useIsLaptop();

  const { data, loading } = useQuery(SO5_APPEARANCE_DIALOG_QUERY, {
    variables: { id: so5AppearanceId },
  });

  const { composeTeamBenchObject } = data?.so5 || {};

  const anyGameStarted = composeTeamBenchObject?.eligiblePlayerGameScores.some(
    pgs => isGameStarted(pgs.anyGame.status)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={
        composeTeamBenchObject &&
        composeTeamBenchObject.eligiblePlayerGameScores.length > 2
          ? 'md'
          : 'sm'
      }
      fullWidth
      title={
        !!composeTeamBenchObject &&
        isType(composeTeamBenchObject, 'So5Appearance') && (
          <Horizontal center>
            <HeadlineXS as="h3">
              <Horizontal as="span">
                <span>
                  <FormattedMessage {...fantasy.playerPerformance} />
                </span>
                <span>-</span>
                <span>
                  {formatDate(composeTeamBenchObject.so5Fixture.startDate, {
                    month: 'short',
                  })}
                </span>
                <span>
                  {formatDate(composeTeamBenchObject.so5Fixture.startDate, {
                    day: '2-digit',
                  })}
                  -
                  {formatDate(composeTeamBenchObject.so5Fixture.endDate, {
                    day: '2-digit',
                  })}
                </span>
              </Horizontal>
            </HeadlineXS>
          </Horizontal>
        )
      }
    >
      <Root className={classNames({ loading })} gap={5}>
        {!!loading && <LoadingIndicator />}
        {!!composeTeamBenchObject && (
          <>
            {(isType(composeTeamBenchObject, 'So5Appearance') ||
              isType(
                composeTeamBenchObject,
                'ThresholdPickerTaskAppearance'
              )) && (
              <BriefSo5AppearanceScore so5Appearance={composeTeamBenchObject} />
            )}
            {composeTeamBenchObject.eligiblePlayerGameScores.length === 1 && (
              <BriefGame
                game={
                  composeTeamBenchObject.eligiblePlayerGameScores[0].anyGame
                }
                team={
                  composeTeamBenchObject.eligiblePlayerGameScores[0]
                    .anyPlayerGameStats.anyTeam
                }
                onClick={
                  onClickGame
                    ? () => {
                        const { id: gameId, statusTyped: gameStatus } =
                          composeTeamBenchObject.eligiblePlayerGameScores[0]
                            .anyGame;
                        track('Open Match View', {
                          gameId,
                          gameStatus,
                          deviceType: `web_${isLaptopAndAbove ? 'desktop' : 'mobile'}`,
                          interactionContext: 'appearance_dialog',
                        });
                        onClickGame(
                          composeTeamBenchObject.eligiblePlayerGameScores[0]
                            .anyGame.id
                        );
                      }
                    : undefined
                }
              />
            )}
            {!!showUpsell && (
              <PrimaryBuyForAPlayer
                playerSlug={composeTeamBenchObject.anyPlayer.slug}
              />
            )}
            {!!(
              ('scoreStatus' in composeTeamBenchObject &&
                composeTeamBenchObject.scoreStatus !==
                  ScoreStatus.DID_NOT_PLAY &&
                anyGameStarted) ||
              composeTeamBenchObject.eligiblePlayerGameScores.length > 1
            ) && (
              <PlayerGameScoreStats
                playerGameScores={
                  composeTeamBenchObject.eligiblePlayerGameScores
                }
              />
            )}
            {sport !== Sport.FOOTBALL && (
              <NewsUpdatesWrapper>
                <NewsTitleContent />
                <NewsUpdates
                  updates={composeTeamBenchObject.anyPlayer.news}
                  playerName={composeTeamBenchObject.anyPlayer.displayName}
                />
              </NewsUpdatesWrapper>
            )}
          </>
        )}
      </Root>
    </Dialog>
  );
};
